import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { Search, Zap } from "lucide-react";
import { generateEmbedding } from "../lib/ml";
import { getAllBlocks } from "../lib/db";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function SearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("exact");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
    else {
      setQ("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const v = q.trim();
    if (!v) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        if (mode === "exact") {
          const data = await getAllBlocks();
          const lowered = v.toLowerCase();
          const matches = data.filter(b => 
            (b.type === "text" && b.content && b.content.toLowerCase().includes(lowered)) || 
            (b.type === "image" && b.caption && b.caption.toLowerCase().includes(lowered))
          ).slice(0, 30);
          setResults(matches);
        } else {
          // Semantic search
          const emb = await generateEmbedding(v);
          if (emb) {
            const { data } = await api.post("/blocks/search/semantic", { query_embedding: emb, limit: 30 });
            setResults(data);
          }
        }
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, mode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden gap-0" data-testid="search-dialog">
        <DialogTitle className="sr-only">Search the stream</DialogTitle>
        <DialogDescription className="sr-only">Search across thoughts, captions and timestamps in your stream.</DialogDescription>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search your stream…"
            className="flex-1 bg-transparent font-serif-journal text-base focus:outline-none"
            data-testid="search-input"
          />
          <button
            onClick={() => setMode(mode === "exact" ? "semantic" : "exact")}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
              mode === "semantic" 
                ? "bg-primary/20 text-primary border border-primary/30" 
                : "text-muted-foreground hover:bg-secondary"
            }`}
          >
            <Zap className="w-3 h-3" />
            {mode === "semantic" ? "AI Semantic" : "Exact"}
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto px-4 py-2">
          {q && !loading && results.length === 0 && (
            <p className="meta-label text-muted-foreground py-6 text-center">no echoes found</p>
          )}
          {results.map((b) => (
            <div key={b.id} className="py-3 border-b border-border last:border-b-0" data-testid={`search-result-${b.id}`}>
              <div className="meta-label text-muted-foreground mb-1">{formatDate(b.created_at)}</div>
              {b.type === "text" && (
                <p className="font-serif-journal text-base leading-snug line-clamp-3">{b.content}</p>
              )}
              {b.type === "image" && (
                <p className="font-serif-journal italic text-sm text-muted-foreground">image · {b.caption || "untitled"}</p>
              )}
              {b.type === "drawing" && (
                <p className="font-serif-journal italic text-sm text-muted-foreground">a drawing</p>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
